import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { IMAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "nova_welcome_seen";
const PREBOOK_TOTAL = 20;

export default function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null); // null = loading

  useEffect(() => {
    axios.get(`${API}/plots`)
      .then((r) => {
        const taken = r.data.filter((p) => p.status === "reserved" || p.status === "sold").length;
        setRemaining(Math.max(0, PREBOOK_TOTAL - taken));
      })
      .catch(() => setRemaining(PREBOOK_TOTAL));
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setOpen(true), 1400);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    sessionStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const isEnquiry = remaining !== null && remaining <= 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quick-lead`, { name, phone });
      toast.success(isEnquiry ? "Thank you! Our team will get back to you shortly." : "Thank you! Our team will call you to confirm your pre-booking.");
      close();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={close}
          data-testid="welcome-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative grid w-full max-w-3xl overflow-hidden rounded-sm bg-[var(--cream)] shadow-2xl md:grid-cols-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/20 text-white transition-colors hover:bg-black/40"
              data-testid="welcome-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="relative hidden md:block">
              <img src={IMAGES.hero} alt="Nova Royale by N Murthy Groups" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e1416]/80 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--gold-bright)]">N Murthy Groups</p>
                <p className="mt-1 font-serif-display text-2xl italic text-white">Nova Royale</p>
              </div>
            </div>

            <div className="p-8 md:p-10">
              {isEnquiry ? (
                <>
                  <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Get in touch</p>
                  <h2 className="mt-3 font-serif-display text-3xl leading-tight tracking-tight text-[var(--ink)] md:text-4xl">
                    Have a question? Ask us.
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">
                    Our pre-booking phase is now full, but we'd love to help. Leave your details and the team at <strong>N Murthy Groups</strong> will get back to you about <em>Nova Royale</em>.
                  </p>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)]/15 px-3 py-1" data-testid="prebook-badge">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--gold-hover)]" />
                    <span className="text-[11px] font-600 uppercase tracking-[0.25em] text-[var(--gold-hover)]">Pre-Bookings Open</span>
                  </div>
                  <h2 className="mt-4 font-serif-display text-3xl leading-tight tracking-tight text-[var(--ink)] md:text-4xl">
                    Only{" "}
                    <span className="text-[var(--gold-hover)]" data-testid="prebook-remaining">
                      {remaining === null ? "…" : remaining}
                    </span>{" "}
                    of {PREBOOK_TOTAL} plots left
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">
                    Reserve your plot at <em>Nova Royale</em> during our exclusive pre-booking phase. Share your details and the team at <strong>N Murthy Groups</strong> will call you to confirm.
                  </p>
                </>
              )}

              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="welcome-form">
                <input
                  className="w-full border-b border-[var(--cream-3)] bg-transparent py-3 text-lg text-[var(--ink)] outline-none placeholder:text-[var(--ink-soft)]/50 focus:border-[var(--gold)]"
                  placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="welcome-name"
                />
                <input
                  className="w-full border-b border-[var(--cream-3)] bg-transparent py-3 text-lg text-[var(--ink)] outline-none placeholder:text-[var(--ink-soft)]/50 focus:border-[var(--gold)]"
                  placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)}
                  data-testid="welcome-phone"
                />
                <button
                  type="submit" disabled={loading}
                  className="w-full rounded-sm bg-[var(--gold)] py-4 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[var(--gold-bright)] disabled:opacity-60"
                  data-testid="welcome-submit"
                >
                  {loading ? "Sending…" : isEnquiry ? "Request a Call Back" : "Reserve My Plot"}
                </button>
              </form>
              <button onClick={close} className="mt-4 text-xs uppercase tracking-[0.2em] text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="welcome-later">
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
